import React from 'react'
import { Box, Typography } from '@mui/material'
import { DeleteOutlined, DragIndicatorOutlined, ImageOutlined } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { CanvasItemCard, DeleteButton, DragHandle, CanvasItemIcon } from './DesignerCanvasSection.styles'
import { IInstructionComponentInstance } from 'src/types/report-designer'
import { FormFieldType } from 'src/enums'
import { IFormSchemaColumn } from 'src/generated-types/form-schema'

export interface ICanvasInstructionItemProps {
  item: IInstructionComponentInstance
  componentIcon: React.ReactNode
  isSelected: boolean
  accentColor: string
  isDragging: boolean
  isDragOver: boolean
  columns?: IFormSchemaColumn[]
  onSelectItem: (id: string) => void
  onDeleteItem: (id: string) => void
  onDragStart: (e: React.DragEvent) => void
  onDragOver: (e: React.DragEvent) => void
  onDrop: (e: React.DragEvent) => void
  onDragEnd: () => void
}

export const CanvasInstructionItem: React.FC<ICanvasInstructionItemProps> = ({
  item,
  componentIcon,
  isSelected,
  accentColor,
  isDragging,
  isDragOver,
  columns,
  onSelectItem,
  onDeleteItem,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
}) => {
  const { t: translate } = useTranslation(['dynamic-report'])

  const renderColumnPreview = (fieldType: FormFieldType | undefined, fieldName: string): React.ReactNode => {
    switch (fieldType) {
      case FormFieldType.instructionImage:
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', color: accentColor }}>
            <ImageOutlined fontSize="large" />
          </Box>
        )
      case FormFieldType.instructionFile:
        return (
          <Typography
            variant="body1"
            sx={{ color: 'primary.main', textDecoration: 'underline', cursor: 'default' }}
            noWrap
          >
            {fieldName}
          </Typography>
        )
      default:
        return (
          <Typography variant="body1" color="text.secondary" noWrap>
            {translate('sample-instruction-text')}
          </Typography>
        )
    }
  }

  return (
    <CanvasItemCard
      key={item.id}
      isSelected={isSelected}
      accentColor={accentColor}
      isDragging={isDragging}
      isDragOver={isDragOver}
      onClick={() => onSelectItem(item.id)}
      draggable
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
      sx={{ flexDirection: 'column', alignItems: 'stretch' }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <DragHandle>
          <DragIndicatorOutlined fontSize="small" />
        </DragHandle>
        <CanvasItemIcon accentColor={accentColor}>{componentIcon}</CanvasItemIcon>
        <Typography variant="body2" fontWeight={600} fontSize="0.8rem" noWrap sx={{ flex: 1, minWidth: 0 }}>
          {translate(item.label, { ns: 'dynamic-report' })}
        </Typography>
        <DeleteButton
          size="small"
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation()
            onDeleteItem(item.id)
          }}
          title={translate('delete-item')}
        >
          <DeleteOutlined fontSize="small" />
        </DeleteButton>
      </Box>
      {item.columns.length > 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 0.5 }}>
          {item.columns.map(({ id, schemaFieldType, fieldName }) => (
            <Box key={id} paddingLeft={'30px'}>
              {renderColumnPreview(schemaFieldType ?? undefined, fieldName)}
            </Box>
          ))}
        </Box>
      ) : (
        <Typography variant="caption" color="text.disabled" fontStyle="italic" sx={{ mt: 0.5 }}>
          {translate('no-columns')}
        </Typography>
      )}
    </CanvasItemCard>
  )
}
